import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useMembersApi } from '@/hooks/useMembersApi';
import { usePermissions } from '@/hooks/usePermissions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, CheckCircle, Loader2, LogOut, Trophy, Users } from 'lucide-react';
import { toast } from 'sonner';

type MemberEventSchedule = {
  event_date: string;
  start_at_utc?: string | null;
  event_timezone?: string | null;
};

function dateKeyInTimezone(value: Date, timezone?: string | null): string {
  const resolvedTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: resolvedTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(value);
    const values = Object.fromEntries(
      parts.filter((part) => part.type !== 'literal').map((part) => [part.type, part.value])
    );
    if (values.year && values.month && values.day) {
      return `${values.year}-${values.month}-${values.day}`;
    }
  } catch {
    // Fall back to the browser's local calendar when the stored timezone is invalid.
  }
  return [value.getFullYear(), value.getMonth() + 1, value.getDate()]
    .map((part, index) => (index === 0 ? String(part) : String(part).padStart(2, '0')))
    .join('-');
}

export default function MemberEvents() {
  const membersApi = useMembersApi();
  const { hasPermission } = usePermissions();
  const canSignUpForEvents = hasPermission('sign_up_events');
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['member-events'],
    queryFn: async () => {
      const response = await membersApi.getEvents();
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (eventId: string) => {
      const response = await membersApi.registerForEvent(eventId);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Registered for event!');
      queryClient.invalidateQueries({ queryKey: ['member-events'] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to register');
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: async (eventId: string) => {
      const response = await membersApi.withdrawFromEvent(eventId);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Withdrawn from event');
      queryClient.invalidateQueries({ queryKey: ['member-events'] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to withdraw');
    },
  });

  const formatDate = (event: {
    event_date: string;
    start_time?: string | null;
    end_time?: string | null;
    event_timezone?: string | null;
  }) => {
    const datePart = event.event_date.slice(0, 10);
    const [year, month, day] = datePart.split('-').map(Number);
    const label =
      year && month && day
        ? new Date(year, month - 1, day).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : event.event_date;
    return event.start_time && event.end_time
      ? `${label} · ${event.start_time}–${event.end_time} ${event.event_timezone || 'UTC'}`
      : label;
  };

  const getEventStatus = (event: MemberEventSchedule) => {
    const now = new Date();
    const startAt = event.start_at_utc ? new Date(event.start_at_utc) : null;
    if (startAt && !Number.isNaN(startAt.getTime())) {
      if (startAt < now) return 'past';
      return dateKeyInTimezone(startAt, event.event_timezone) ===
        dateKeyInTimezone(now, event.event_timezone)
        ? 'today'
        : 'upcoming';
    }

    const legacyTimestamp = event.event_date.length > 10 ? new Date(event.event_date) : null;
    if (legacyTimestamp && !Number.isNaN(legacyTimestamp.getTime())) {
      if (legacyTimestamp < now) return 'past';
      return dateKeyInTimezone(legacyTimestamp, event.event_timezone) ===
        dateKeyInTimezone(now, event.event_timezone)
        ? 'today'
        : 'upcoming';
    }

    const datePart = event.event_date.slice(0, 10);
    const today = dateKeyInTimezone(now, event.event_timezone);
    if (datePart < today) return 'past';
    if (datePart === today) return 'today';
    return 'upcoming';
  };

  const statusColors: Record<string, string> = {
    past: 'bg-muted text-muted-foreground border-muted',
    today: 'bg-green-500/10 text-green-500 border-green-500/20',
    upcoming: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  };

  const statusLabels: Record<string, string> = {
    past: 'Completed',
    today: 'Today',
    upcoming: 'Upcoming',
  };

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
        <p className="text-destructive">Failed to load events</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  const events = data?.events || [];
  const upcomingEvents = events.filter((event) => getEventStatus(event) !== 'past');
  const pastEvents = events.filter((event) => getEventStatus(event) === 'past');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gradient-gold">Events</h1>
        <p className="mt-1 text-muted-foreground">
          Discover and sign up for gym events and competitions.
        </p>
      </div>

      {/* Upcoming Events */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-4">Upcoming Events</h2>
        {upcomingEvents.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="flex flex-col items-center justify-center text-center">
                <div className="rounded-full bg-muted p-4 mb-4">
                  <Trophy className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-1">No upcoming events</h3>
                <p className="text-muted-foreground max-w-md">
                  Check back later for new events and competitions at your gym.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {upcomingEvents.map((event, index) => {
              const status = getEventStatus(event);
              const isPending = registerMutation.isPending || withdrawMutation.isPending;
              const pendingEventId = registerMutation.variables || withdrawMutation.variables;

              return (
                <Card
                  key={event.id}
                  className="animate-fade-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg">{event.name}</CardTitle>
                      <Badge variant="outline" className={statusColors[status]}>
                        {statusLabels[status]}
                      </Badge>
                    </div>
                    <CardDescription className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {formatDate(event)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="h-4 w-4" />
                      {event.participant_count} registered
                    </div>

                    {!canSignUpForEvents ? null : event.is_signed_up ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-green-500">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-sm font-medium">You're registered!</span>
                        </div>
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => withdrawMutation.mutate(event.id)}
                          disabled={isPending && pendingEventId === event.id}
                        >
                          {isPending && pendingEventId === event.id ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <LogOut className="h-4 w-4 mr-2" />
                          )}
                          Withdraw
                        </Button>
                      </div>
                    ) : (
                      <Button
                        className="w-full"
                        onClick={() => registerMutation.mutate(event.id)}
                        disabled={isPending && pendingEventId === event.id}
                      >
                        {isPending && pendingEventId === event.id ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Trophy className="h-4 w-4 mr-2" />
                        )}
                        Sign Up
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Past Events */}
      {pastEvents.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-4 text-muted-foreground">Past Events</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {pastEvents.map((event) => (
              <Card key={event.id} className="opacity-60">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg">{event.name}</CardTitle>
                    <Badge variant="outline" className={statusColors.past}>
                      Completed
                    </Badge>
                  </div>
                  <CardDescription className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {formatDate(event)}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Users className="h-4 w-4" />
                      {event.participant_count} participated
                    </div>
                    {event.is_signed_up && (
                      <Badge variant="outline" className="text-green-500 border-green-500/20">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Attended
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
